<template>
  <CustomContainer
    padding="p-3"
    :id="`${source}_container_info_item`"
    :class="getHeightClass"
  >
    <SkeletonLoader
      :text="title"
      :page="loaderPages.CONTAINER_INFO_ITEM"
      container-height="h-full"
      v-if="isLoading"
    />

    <EmptyScreen v-if="isEmpty" :source="`${source}_container_info_item`" />

    <div
      v-if="!isEmpty && !isLoading"
      class="flex flex-col size-full font-medium text_color"
    >
      <div class="flex flex-row gap-2 justify-between">
        <div class="flex flex-row gap-2">
          <ThemeImage
            v-if="back"
            class="size-4 cursor-pointer mt-[2px]"
            :icon="imageNames.BACK_ARROW_VARIANT1_GREY"
            alt="back_arrow"
            @click="back"
          />

          <div class="flex flex-col gap-1">
            <div class="flex flex-row items-center gap-1">
              <span class="text-sm">{{ title }}</span>
              <a-tooltip
                v-if="titleTooltip"
                placement="top"
                :title="titleTooltip"
              >
                <ThemeImage
                  class="size-4 cursor-pointer"
                  :icon="imageNames.INFO_GREY"
                  alt="info_icon"
                />
              </a-tooltip>
            </div>
            <span v-if="description" class="text-xs secondary_color">{{
              description
            }}</span>
          </div>
        </div>

        <CustomDropdown
          v-if="showExportOption"
          maxHeight="200px"
          gap="10px"
          position="end"
        >
          <template #trigger>
            <ThemeImage
              class="size-4 cursor-pointer"
              :icon="imageNames.MORE_VERTICAL"
              alt="deals_icon"
            />
          </template>
          <template #default>
            <div
              v-for="item in threeDotsDropdownItems"
              :key="item.key"
              class="flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
              @click="item.action"
              :class="[
                item.key === 'export' && isExportDisabled
                  ? 'cursor-not-allowed hover:bg-transparent'
                  : 'dropdown_item',
              ]"
            >
              <ThemeImage class="size-4" :icon="item.icon" alt="dropdown_icons" />
              <span
                class="proxima_nova text-sm"
                :class="{
                  secondary_color: item.key === 'export' && isExportDisabled,
                }"
                >{{ item.text }}</span
              >
            </div>
          </template>
        </CustomDropdown>
      </div>

      <div
        v-if="value !== undefined && value !== null"
        class="size-full flex items-center justify-center"
      >
        <a-tooltip v-if="showTooltip" placement="top">
          <template v-slot:title>
            <span>{{ fullValue || value }}</span>
          </template>
          <span :class="`text-[${valueSize}px] font-semibold`">{{ value }}</span>
        </a-tooltip>
        <span v-else :class="`text-[${valueSize}px] font-semibold`">{{ value }}</span>
      </div>

      <slot v-else></slot>
    </div>
  </CustomContainer>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";

import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import { PropType } from "vue";

import {imageNames} from "@/src/constants/imageConstants.js";


interface DropdownItem {
  key: string;
  text: string;
  icon: any;
  action: () => any;
}

const props = defineProps({
  source: { type: String, required: true },
  title: { type: String, required: true },
  description: { type: String, required: false },
  value: { type: [String, Number], required: false },
  valueSize: { type: Number, default: 54 },
  height: { type: [Number, String], default: 220 },
  isLoading: { type: Boolean, default: false },
  isEmpty: { type: Boolean, default: false },
  back: { type: Function, default: null },
  showExportOption: { type: Boolean, default: false },
  threeDotsDropdownItems: {
    type: Array as PropType<DropdownItem[]>,
    default: () => [],
  },
  isExportDisabled: { type: Boolean, default: false },
  showTooltip: { type: Boolean, default: false },
  fullValue: { type: [String, Number], required: false },
  titleTooltip: { type: String, required: false },
});

const getHeightClass = computed(() => {
  if (props.height === "fit-content") {
    return "h-fit";
  } else if (props.height === "full") {
    return "h-full";
  } else if (typeof props.height === "number") {
    return `h-[${props.height}px]`;
  } else {
    return `h-[${props.height}]`;
  }
});
</script>
